import { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  SelectChangeEvent,
  Button,
} from '@mui/material';
import { Delete, Send, Add } from '@mui/icons-material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import COLORS from '@/resources/style/Themes';
import { NavigatorDataGrid } from '@/components/data/NavigatorDataGrid';
import { EmergencyEmailContact } from '../../../../generated-client/models';
import {
  StringKeys,
  SuccessStringKeys,
  ErrorStringKeys,
} from '@/resources/strings/StringKeys';
import { useToast } from '@/context_providers/toast_context/ToastContext';
import { sendTestEmergencyEmail } from '@/repository/OrganizationDAO';
import AddEmailModal from './AddEmailModal';
import ConfirmModal from '@/components/modals/utils/ConfirmModal';
import { ResponseError } from '../../../../generated-client/runtime';
import { parseApiError } from '@/utils/api/parseApiError';
import { useContactMutationError } from '@/hooks/useContactMutationError';

// Row type for DataGrid
interface EmailContactRow {
  id: string;
  email: string;
  name: string;
  language_code: string;
}

interface EmergencyEmailManagementProps {
  // Email contacts data
  emailContacts: { [key: string]: EmergencyEmailContact } | undefined;

  // Callback for deleting an email
  onDelete: (email: string) => Promise<void>;

  // Callback for updating email language
  onUpdateLanguage: (email: string, languageCode: string) => Promise<void>;

  // Callback for adding a new email
  onAddEmail: (
    email: string,
    name: string,
    languageCode: string,
  ) => Promise<void>;

  // Loading state
  loading?: boolean;

  // Optional empty state message
  emptyMessage?: string;

  // Optional tab control component to render alongside action buttons
  tabControl?: React.ReactNode;
}

const EmergencyEmailManagement = ({
  emailContacts,
  onDelete,
  onUpdateLanguage,
  onAddEmail,
  loading = false,
  emptyMessage,
  tabControl,
}: EmergencyEmailManagementProps) => {
  const { t } = useTranslation();
  const { showSuccessToast, showErrorToast } = useToast();
  const showContactMutationError = useContactMutationError();
  const [sendingTestEmail, setSendingTestEmail] = useState<string | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<{
    email: string;
    name: string;
  } | null>(null);

  // Transform email contacts to DataGrid rows
  const emailContactRows: EmailContactRow[] = useMemo(() => {
    if (!emailContacts) return [];

    return Object.entries(emailContacts).map(
      ([key, contact]: [string, EmergencyEmailContact]) => ({
        id: key,
        email: contact.email,
        name: contact.name,
        language_code: contact.language_code,
      }),
    );
  }, [emailContacts]);

  // Handle delete for email contact
  const handleDelete = useCallback((email: string, name: string) => {
    setPendingDelete({ email, name });
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!pendingDelete) return;
    try {
      await onDelete(pendingDelete.email);
      setPendingDelete(null);
      showSuccessToast(
        t(SuccessStringKeys.SUCCESSFULLY_DELETED_THE_EMERGENCY_EMAIL),
      );
    } catch (err) {
      console.error('Error deleting email contact:', err);
      await showContactMutationError(
        err,
        ErrorStringKeys.FAILED_TO_REMOVE_EMERGENCY_EMAIL,
      );
    }
  }, [pendingDelete, onDelete, showSuccessToast, showContactMutationError, t]);

  // Handle language change for email contact
  const handleLanguageChange = useCallback(
    async (email: string, event: SelectChangeEvent<string>) => {
      const newLanguageCode = event.target.value;

      try {
        await onUpdateLanguage(email, newLanguageCode);
        showSuccessToast(
          t(SuccessStringKeys.SUCCESSFULLY_UPDATED_EMAIL_LANGUAGE_PREFERENCE),
        );
      } catch (err) {
        console.error('Error updating email language:', err);
        await showContactMutationError(
          err,
          ErrorStringKeys.FAILED_TO_UPDATE_EMAIL_LANGUAGE,
        );
      }
    },
    [onUpdateLanguage, showSuccessToast, showContactMutationError, t],
  );

  // Handle send test email
  const handleSendTest = useCallback(
    async (email: string) => {
      setSendingTestEmail(email);
      try {
        await sendTestEmergencyEmail(email);
        showSuccessToast(t(StringKeys.TEST_EMAIL_SENT, { email }));
      } catch (err) {
        console.error('Error sending test email:', err);
        showErrorToast(t(ErrorStringKeys.FAILED_TO_SEND_TEST_EMERGENCY_EMAIL));
      } finally {
        setSendingTestEmail(null);
      }
    },
    [showSuccessToast, showErrorToast, t],
  );

  // Handle add email
  const handleAddEmail = useCallback(
    async (email: string, name: string, languageCode: string) => {
      try {
        await onAddEmail(email, name, languageCode);
        showSuccessToast(
          t(SuccessStringKeys.SUCCESSFULLY_ADDED_THE_EMERGENCY_EMAIL_CONTACT),
        );
      } catch (err) {
        const { code } = await parseApiError(err);
        if (code === 'PLAN_RESPOND_REQUIRED') {
          await showContactMutationError(
            err,
            ErrorStringKeys.FAILED_TO_ADD_EMERGENCY_EMAIL,
          );
          throw err; // Re-throw to let modal handle it
        }
        // Check if it's a ResponseError from the generated API client
        if (err instanceof ResponseError) {
          // Check if it's a 400 status (bad request)
          if (err.response.status === 400) {
            // Parse the response body to get the exception message
            const errorData = await err.response.json();
            if (
              errorData.exceptionMessage &&
              errorData.exceptionMessage
                .toLowerCase()
                .includes('already exists')
            ) {
              showErrorToast(
                t(
                  ErrorStringKeys.FAILED_TO_ADD_EMERGENCY_EMAIL_EMAIL_ALREADY_EXISTS,
                ),
              );
              return; // Don't re-throw since we handled this case
            }
          }
        }
        console.error('Error adding email contact:', err);
        showErrorToast(t(ErrorStringKeys.FAILED_TO_ADD_EMERGENCY_EMAIL));
        throw err; // Re-throw to let modal handle it
      }
    },
    [onAddEmail, showSuccessToast, showErrorToast, showContactMutationError, t],
  );

  // Language options for the dropdown
  const languageOptions = useMemo(
    () => [
      { code: 'en-US', label: t(StringKeys.ENGLISH) },
      { code: 'es', label: t(StringKeys.SPANISH) },
      { code: 'fra', label: t(StringKeys.FRENCH) },
      { code: 'deu', label: t(StringKeys.GERMAN) },
      { code: 'it', label: t(StringKeys.ITALIAN) },
      { code: 'pt', label: t(StringKeys.PORTUGUESE) },
      { code: 'sv-SE', label: t(StringKeys.SWEDISH) },
      { code: 'nb', label: t(StringKeys.NORWEGIAN) },
      { code: 'da', label: t(StringKeys.DANISH) },
      { code: 'fi', label: t(StringKeys.FINNISH) },
      { code: 'el', label: t(StringKeys.GREEK) },
      { code: 'pl', label: t(StringKeys.POLISH) },
    ],
    [t],
  );

  // Email contacts DataGrid columns
  const emailColumns: GridColDef<EmailContactRow>[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: t(StringKeys.NAME),
        minWidth: 200,
        flex: 1,
        display: 'flex',
      },
      {
        field: 'email',
        headerName: t(StringKeys.EMAIL),
        minWidth: 250,
        flex: 1,
        display: 'flex',
      },
      {
        field: 'language_code',
        headerName: t(StringKeys.LANGUAGE),
        minWidth: 150,
        flex: 0.75,
        display: 'flex',
        renderCell: (params: GridRenderCellParams<EmailContactRow>) => {
          return (
            <FormControl
              size="small"
              fullWidth
              onClick={(e) => e.stopPropagation()}
            >
              <Select
                value={params.row.language_code}
                onChange={(event) =>
                  handleLanguageChange(params.row.email, event)
                }
                disabled={loading}
                sx={{
                  '& .MuiSelect-select': {
                    py: 0.75,
                  },
                }}
              >
                {languageOptions.map((lang) => (
                  <MenuItem key={lang.code} value={lang.code}>
                    {lang.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        },
      },
      {
        field: 'actions',
        headerName: t(StringKeys.ACTION),
        minWidth: 120,
        flex: 0.5,
        display: 'flex',
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams<EmailContactRow>) => (
          <Box
            sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}
            onClick={(e) => e.stopPropagation()}
          >
            <Tooltip title={t(StringKeys.SEND_TEST_EMAIL)}>
              <span>
                <IconButton
                  size="small"
                  onClick={() => handleSendTest(params.row.email)}
                  disabled={loading || sendingTestEmail === params.row.email}
                  sx={{
                    color: COLORS.SAFEGUARD_BLUE,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 123, 255, 0.1)',
                    },
                  }}
                >
                  <Send fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title={t(StringKeys.DELETE)}>
              <span>
                <IconButton
                  size="small"
                  onClick={() =>
                    handleDelete(params.row.email, params.row.name)
                  }
                  disabled={loading}
                  sx={{
                    color: COLORS.RED,
                    '&:hover': {
                      backgroundColor: 'rgba(244, 67, 54, 0.1)',
                    },
                  }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [
      t,
      languageOptions,
      loading,
      sendingTestEmail,
      handleLanguageChange,
      handleSendTest,
      handleDelete,
    ],
  );

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
    >
      {/* Tab Control and Add Email Button */}
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        {tabControl && <Box>{tabControl}</Box>}
        <Button
          variant="contained"
          color="secondary"
          startIcon={<Add />}
          onClick={() => setIsAddModalOpen(true)}
        >
          {t(StringKeys.ADD_EMAIL)}
        </Button>
      </Box>

      {/* Bounded-height wrapper: rows scroll inside the grid and the pagination
          footer stays pinned to the bottom (mirrors EmergencyRespondersPane). */}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <NavigatorDataGrid
          height="100%"
          rows={emailContactRows}
          columns={emailColumns}
          getRowId={(row) => row.id}
          initialPageSize={10}
          pageSizeOptions={[10, 25, 50]}
          slots={{
            noRowsOverlay: () => (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  py: 8,
                }}
              >
                <Typography
                  sx={{
                    color: COLORS.GREY,
                    fontStyle: 'italic',
                    fontSize: 14,
                  }}
                >
                  {emptyMessage || t(StringKeys.NO_EMAIL_CONTACTS_CONFIGURED)}
                </Typography>
              </Box>
            ),
          }}
          sx={{
            '& .MuiDataGrid-columnHeader': {
              backgroundColor: COLORS.SAFEGUARD_BLUE,
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              color: COLORS.WHITE,
              fontWeight: 'bold',
            },
            '& .MuiDataGrid-row': {
              py: 0,
            },
            '& .MuiDataGrid-cell': {
              py: 0.5,
            },
          }}
        />
      </Box>

      {/* Add Email Modal */}
      <AddEmailModal
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSave={handleAddEmail}
      />

      <ConfirmModal
        show={!!pendingDelete}
        onHide={() => setPendingDelete(null)}
        confirmTitle={t(StringKeys.CONFIRM_REMOVAL)}
        confirmMessage={
          pendingDelete
            ? t(StringKeys.REMOVE_EMAIL_FROM_EMERGENCY_RECIPIENTS, {
                email: pendingDelete.email,
                name: pendingDelete.name,
              })
            : ''
        }
        confirm={handleConfirmDelete}
      />
    </Box>
  );
};

export default EmergencyEmailManagement;
